import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from '../Styles/Toast.module.css';

const ToastContext = createContext(undefined);

/**
 * Käyttö:
 * <ToastProvider> ... </ToastProvider>                // auto: desktop top-right, mobile bottom-center
 * <ToastProvider position="top-left"> ... </ToastProvider> // pakotettu sijainti
 *
 * const { showToast } = useToast();
 * showToast("Tallennettu onnistuneesti", "success", 3000);
 */
export function ToastProvider({ children, position = 'auto' }) {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timersRef = useRef(new Map()); // id -> timeoutId

  // --- poistaminen ---
  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const tm = timersRef.current.get(id);
    if (tm) {
      clearTimeout(tm);
      timersRef.current.delete(id);
    }
  }, []);

  // --- näyttäminen ---
  const showToast = useCallback(
    (message, type = 'info', durationMs = 3000) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, type }]);
      const timeoutId = window.setTimeout(() => remove(id), durationMs);
      timersRef.current.set(id, timeoutId);
    },
    [remove]
  );

  // --- siivous unmountissa ---
  useEffect(() => {
    return () => {
      timersRef.current.forEach((tm) => clearTimeout(tm));
      timersRef.current.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  // --- sijainnin valinta ---
  const pickClass = useCallback((pos) => {
    const map = {
      'top-right': styles.posTopRight,
      'top-center': styles.posTopCenter,
      'top-left': styles.posTopLeft,
      'bottom-right': styles.posBottomRight,
      'bottom-center': styles.posBottomCenter,
      'bottom-left': styles.posBottomLeft,
    };
    return map[pos] || styles.posTopRight;
  }, []);

  const [posClass, setPosClass] = useState(() =>
    position === 'auto'
      ? pickClass(window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? 'bottom-center' : 'top-right')
      : pickClass(position)
  );

  // päivitä automaattisesti ruudun leveyden mukaan, jos position === 'auto'
  useEffect(() => {
    if (position !== 'auto') {
      setPosClass(pickClass(position));
      return;
    }
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => {
      setPosClass(pickClass(mql.matches ? 'bottom-center' : 'top-right'));
    };
    update();
    if (mql.addEventListener) {
      mql.addEventListener('change', update);
      return () => mql.removeEventListener('change', update);
    } else {
      // Safari vanhemmat
      mql.addListener(update);
      return () => mql.removeListener(update);
    }
  }, [position, pickClass]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={`${styles.viewport} ${posClass}`} aria-live="polite" aria-atomic="true">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`${styles.toast} ${
              t.type === 'success' ? styles.success : t.type === 'error' ? styles.error : styles.info
            }`}
            role="status"
          >
            <div className={styles.message}>{t.message}</div>
            <button
              className={styles.close}
              onClick={() => remove(t.id)}
              aria-label="Sulje ilmoitus"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
